import { useState, useEffect, useCallback, useRef, useMemo } from 'react';

import { useVisualization } from '@/context';
import type { CarmView } from '@/visualization/domain/carm-view';
import { CarmCameraBinding } from '@/visualization/services/carm-camera-binding';
import { CarmViewService } from '@/visualization/services/carm-view-service';
import type { CarmEditTarget } from '@/visualization/services/carm-view-service';

export interface CarmViewsSnapshot {
    readonly views: readonly CarmView[];
    readonly selectedIndex: number | null;
    /** Target of the active edit draft, or null when no edit is in progress. */
    readonly editTarget: CarmEditTarget | null;
    readonly isEditDirty: () => boolean;
    readonly deselectView: () => void;
    /** Enter C-arm edit mode for an existing view (index) or a new one. */
    readonly beginEdit: (target: CarmEditTarget) => void;
    readonly deleteView: (index: number) => void;
    readonly renameView: (index: number, name: string) => void;
    readonly resetView: (index: number) => void;
    readonly isPrecomputed: (index: number) => boolean;
    readonly isEdited: (index: number) => boolean;
}

/**
 * @param withCameraBinding when true, owns a CarmCameraBinding that applies
 * selection/edit camera changes to the current layout's 3D viewport.
 * Exactly one consumer per viewer should pass true.
 */
export function useCarmViews(withCameraBinding = false): CarmViewsSnapshot | null {
    const { getServiceManager } = useVisualization();
    const [snapshot, setSnapshot] = useState<CarmViewsSnapshot | null>(null);
    const serviceRef = useRef<CarmViewService | null>(null);
    const bindingRef = useRef<CarmCameraBinding | null>(null);

    const actions = useMemo(
        () => ({
            deselectView: () => serviceRef.current?.deselectView(),
            beginEdit: (target: CarmEditTarget) => serviceRef.current?.beginEdit(target),
            isEditDirty: () => serviceRef.current?.isDraftDirty() ?? false,
            deleteView: (index: number) => serviceRef.current?.deleteView(index),
            renameView: (index: number, name: string) => serviceRef.current?.renameView(index, name),
            resetView: (index: number) => serviceRef.current?.resetView(index),
            isPrecomputed: (index: number) => serviceRef.current?.isPrecomputed(index) ?? true,
            isEdited: (index: number) => serviceRef.current?.isEdited(index) ?? false,
        }),
        []
    );

    const buildSnapshot = useCallback(() => {
        const service = serviceRef.current;
        if (!service) return;

        const views = service.getViews();
        if (views.length === 0) {
            setSnapshot(null);
            return;
        }

        setSnapshot({
            views,
            selectedIndex: service.getSelectedIndex(),
            editTarget: service.getDraft()?.target ?? null,
            ...actions,
        });
    }, [actions]);

    useEffect(() => {
        const sm = getServiceManager();
        const service = sm.getService<CarmViewService>(CarmViewService.SERVICE_NAME);
        serviceRef.current = service;

        if (withCameraBinding) {
            bindingRef.current = new CarmCameraBinding(service, sm);
        }

        if (service.getViews().length > 0) {
            buildSnapshot();
        }

        // EDIT_CHANGED fires per drag frame and never affects this snapshot
        // (only the draft angles change mid-edit, never its target) — skip it
        // to avoid re-rendering the rows
        const unsubs = Object.values(service.EVENTS)
            .filter((event) => event !== service.EVENTS.EDIT_CHANGED)
            .map((event) => service.subscribe(event, buildSnapshot));

        return () => {
            serviceRef.current = null;
            bindingRef.current?.dispose();
            bindingRef.current = null;
            unsubs.forEach((u) => u());
        };
    }, [getServiceManager, buildSnapshot, withCameraBinding]);

    return snapshot;
}
